import { Button, Modal, SearchInput } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { profileSearchWhere } from "./profileSearch";
import css from "./ProfilePickerModal.module.scss";

// Colocated fragment: what the picker shows per hit.
const ProfilePickerModal_Profile = graphql(`
  fragment ProfilePickerModal_Profile on Profile {
    uuid
    listName
    email
    organization {
      uuid
      name
    }
  }
`);

const ProfilePickerModalQuery = graphql(`
  query ProfilePickerModal_Query($where: ProfileWhere) {
    profiles(where: $where, options: { limit: 25, sort: { lastName: ASC } }) {
      ...ProfilePickerModal_Profile
    }
  }
`);

const MIN_SEARCH = 2;

type Props = {
  title: string;
  /** uuids already linked — excluded from the hit list. */
  excludeUuids: string[];
  onClose: () => void;
  onPick: (uuid: string) => void;
};

/**
 * Search-driven single-profile picker. Profiles are global and numerous, so the
 * list stays server-side and capped instead of loading everything up front.
 */
export function ProfilePickerModal({ title, excludeUuids, onClose, onPick }: Props) {
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const search = term.trim();
  const [{ data, fetching }] = useQuery({
    query: ProfilePickerModalQuery,
    variables: { where: profileSearchWhere(search) },
    pause: search.length < MIN_SEARCH,
  });

  const exclude = useMemo(() => new Set(excludeUuids), [excludeUuids]);
  const hits = getFragmentData(ProfilePickerModal_Profile, data?.profiles ?? []).filter(
    (p) => !exclude.has(p.uuid),
  );

  return (
    <Modal isOpen ariaHideApp={false} title={title} onRequestClose={onClose}>
      <SearchInput
        name="profile-search"
        value={term}
        placeholder="Name oder E-Mail …"
        onChange={(e) => {
          setTerm(e.target.value);
          setSelected(null);
        }}
      />

      <div className={css.results}>
        {search.length < MIN_SEARCH ? (
          <span className={css.note}>Mindestens {MIN_SEARCH} Zeichen eingeben.</span>
        ) : fetching ? (
          <span className={css.note}>Sucht …</span>
        ) : hits.length === 0 ? (
          <span className={css.note}>Kein Profil gefunden.</span>
        ) : (
          hits.map((p) => (
            <button
              key={p.uuid}
              type="button"
              className={css.row}
              aria-pressed={selected === p.uuid}
              onClick={() => setSelected(p.uuid)}
            >
              <span className={css.rowMain}>
                <span className={css.rowName}>{p.listName || p.uuid}</span>
                <span className={css.rowMeta}>
                  {[p.organization?.name, p.email].filter(Boolean).join(" · ") || "—"}
                </span>
              </span>
            </button>
          ))
        )}
      </div>

      <div className={css.foot}>
        <Button hierarchy="tertiary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          hierarchy="primary"
          disabled={!selected}
          onClick={() => selected && onPick(selected)}
        >
          Hinzufügen
        </Button>
      </div>
    </Modal>
  );
}
